import React, { useState } from "react";
import { Field, Text } from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import { type ConnectionEditState, type ConnectionEditStateUpdater } from "@onetasklist/shared";
import {
  useOutlookSourceSettingsText,
  useOutlookSourceSettingsViewModel,
} from "@onetasklist/shared/sources/outlook";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { SourceSingleTagPicker } from "./components/SourceSingleTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";

interface OutlookTasksSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
}

export const OutlookTasksSourceSettings: React.FC<
  OutlookTasksSourceSettingsProps
> = ({ editingConnection, onUpdate, sectionOffset }) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const vm = useOutlookSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });
  const text = useOutlookSourceSettingsText(t, "tasks");
  const [folderQuery, setFolderQuery] = useState("");

  if (!vm.isVisible || !vm.isOutlookTasks) {
    return null;
  }

  return (
    <SettingsSectionCard
      sectionOffset={sectionOffset}
      title={t(
        "board.connections.catalogue.sourceSettings.outlooktasks.sectionTasks",
      )}
    >
      <Field orientation="horizontal" label={text.labelText} hint={text.hintText}>
        <SourceSingleTagPicker
          isLoading={vm.isLoading}
          selectedId={
            vm.selectedFolderId === vm.noneOptionValue
              ? undefined
              : vm.selectedFolderId
          }
          selectedLabel={vm.selectedFolderName}
          query={folderQuery}
          placeholder={text.notSetText}
          options={vm.folderOptions.map((folder) => ({
            id: folder.id,
            label: folder.label,
          }))}
          onQueryChange={setFolderQuery}
          onSelect={vm.onSelectFolder}
          clearSelectionText={text.notSetText}
        />
        {vm.isLoading && <Text>{text.loadingText}</Text>}
        {vm.hasError && <Text>{text.loadErrorText}</Text>}
      </Field>
    </SettingsSectionCard>
  );
};
